/**
 * Browser terminal for a VM, rendered with xterm.js.
 *
 * WS contract (unchanged from Phase 3):
 *   - text/binary frames from server → write into xterm
 *   - keystrokes from xterm.onData → send as text frames
 *
 * No resize protocol on the BE today, so we fit the renderer to the
 * container locally but do not signal the guest pty.
 */
import { FitAddon } from "@xterm/addon-fit";
import { Terminal as Xterm } from "@xterm/xterm";
import xtermCss from "@xterm/xterm/css/xterm.css?inline";
import {
  type Component,
  Show,
  createEffect,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import { vmTerminalUrl } from "@/api/vms";

interface Props {
  vmId: string;
  onClose: () => void;
}

// Inject xterm's CSS once. Vite's `?inline` returns the stylesheet as a
// string at build time so we can mount it without a global import.
function ensureXtermStyles() {
  if (typeof document === "undefined") return;
  if (document.getElementById("xterm-styles")) return;
  const style = document.createElement("style");
  style.id = "xterm-styles";
  style.textContent = xtermCss;
  document.head.appendChild(style);
}

const Terminal: Component<Props> = (props) => {
  const [status, setStatus] = createSignal<"connecting" | "open" | "closed">(
    "connecting",
  );
  const [error, setError] = createSignal<string | null>(null);
  let mountEl: HTMLDivElement | undefined;
  let term: Xterm | null = null;
  let fit: FitAddon | null = null;
  let socket: WebSocket | null = null;
  let resizeObs: ResizeObserver | null = null;

  onMount(() => {
    ensureXtermStyles();
    if (!mountEl) return;
    term = new Xterm({
      convertEol: true,
      cursorBlink: true,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 13,
      theme: { background: "#0a0a0a", foreground: "#ddd" },
    });
    fit = new FitAddon();
    term.loadAddon(fit);
    term.open(mountEl);
    fit.fit();

    resizeObs = new ResizeObserver(() => {
      try {
        fit?.fit();
      } catch {
        // ignore: container may be 0×0 mid-transition
      }
    });
    resizeObs.observe(mountEl);

    term.onData((data) => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(data);
      }
    });
  });

  createEffect(() => {
    const url = vmTerminalUrl(props.vmId);
    try {
      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      ws.onopen = () => {
        setStatus("open");
        term?.focus();
      };
      ws.onmessage = (ev) => {
        const data = ev.data;
        if (!term) return;
        if (typeof data === "string") {
          term.write(data);
          return;
        }
        if (data instanceof ArrayBuffer) {
          term.write(new Uint8Array(data));
          return;
        }
        if (data instanceof Blob) {
          data
            .arrayBuffer()
            .then((buf) => term?.write(new Uint8Array(buf)))
            .catch(() => {
              // ignore individual frame decode failures
            });
        }
      };
      ws.onclose = () => setStatus("closed");
      ws.onerror = () => setError("ws error");
      socket = ws;
    } catch (e) {
      setError(String(e));
      setStatus("closed");
    }
  });

  onCleanup(() => {
    socket?.close();
    resizeObs?.disconnect();
    term?.dispose();
  });

  return (
    <section class="vm-terminal">
      <header>
        <strong>terminal</strong>
        <span class={`status-pill status-${status()}`}>{status()}</span>
        <button type="button" onClick={props.onClose}>
          close
        </button>
      </header>
      <Show when={error()}>
        <p class="chat-error">{error()}</p>
      </Show>
      <div class="vm-terminal-xterm" ref={mountEl} />
    </section>
  );
};

export default Terminal;
